import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { TriangleExclamation } from '@styled-icons/fa-solid/TriangleExclamation';

/**
 * Modal to confirm Customs MoP when using EORI Deferment or when customs MoP for the consignee is "select".
 *
 *
 * @param {object} props
 * @param {string} props.type - Modal type indicator (`check` for select mode, otherwise EORI mode).
 * @param {Function} props.onHide - Callback when the modal is closed.
 * @param {Function} props.onConfirm - Callback when confirm is clicked
 * @param {number|null} [props.initialSelection=null] - Preselected option id.
 * @param {boolean} [props.readOnly=false] - Whether the modal is rendered in read-only mode.
 * @param {boolean} props.useTanAccount - When true, uses TAN account wording in the EORI message.
 * @returns {JSX.Element}
 */
const CustomsMoPConfirmationModal = ({
  type,
  onHide,
  onConfirm,
  initialSelection = null,
  readOnly = false,
  useTanAccount,
}) => {
  const [selectedOption, setSelectedOption] = useState(null);

  useEffect(() => {
    setSelectedOption(initialSelection !== null && initialSelection !== undefined ? Number(initialSelection) : null);
  }, [initialSelection]);

  const checkWithCustomerModalInfo = {
    title: 'Customs MoP',
    bodyText: "Check the duty payer's BP notes or confirm with them which Customs MoP to use for this load.",
    options: [
      {
        id: 4,
        text: "Importer's Deferment Account",
      },
      {
        id: 5,
        text: "Importer's Cash Account",
      },
      {
        id: 6,
        text: 'No MoP is required',
      },
    ],
  };

  const eoriModalInfo = {
    title: 'EORI Deferment',
    bodyText: `You are about to submit a dutiable load using EORI's ${useTanAccount ? 'TAN' : 'Deferment'} Account as the customs MoP. Please
            confirm one of the following:`,
    options: [
      {
        id: 1,
        text: (
          <>
            The Potential Liability calculated by a duty-trained agent is <strong>less than £500</strong>
          </>
        ),
      },
      {
        id: 2,
        text: (
          <>
            An invoice has been <strong>issued and paid</strong>
          </>
        ),
      },
      {
        id: 3,
        text: 'There is no duty and/or VAT',
      },
    ],
  };

  const modalData = type === 'check' ? checkWithCustomerModalInfo : eoriModalInfo;

  const handleChange = (optionId) => {
    if (readOnly) return;
    setSelectedOption(optionId);
  };

  const handleConfirm = () => {
    if (selectedOption !== null) {
      onConfirm?.(selectedOption);
    }
  };

  const handleClose = () => {
    onHide?.();
  };

  return (
    <Modal show={!!type} onHide={handleClose} centered size="lg" backdrop={readOnly ? true : 'static'}>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-3">
          <TriangleExclamation className="customs-mop-warning-icon" size={26} aria-hidden="true" />
          <span>{modalData.title}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="customs-mop-info">
          <p>{modalData.bodyText}</p>
        </div>

        <div className="customs-mop-options">
          {modalData.options.map((option) => (
            <label key={option.id} className={`customs-mop-option ${selectedOption === option.id ? 'selected' : ''}`}>
              <input
                type="radio"
                name="customs-mop-option"
                checked={selectedOption === option.id}
                onChange={() => handleChange(option.id)}
                disabled={readOnly}
              />
              <span>{option.text}</span>
            </label>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer className="customs-mop-footer">
        {!readOnly && (
          <div className="d-flex w-100 justify-content-end gap-2">
            <button className="blue-button-teams green-btn" onClick={handleClose}>
              Cancel
            </button>
            <button className="blue-button-teams" onClick={handleConfirm} disabled={selectedOption === null}>
              Confirm
            </button>
          </div>
        )}
        {readOnly && (
          <div className="d-flex w-100 justify-content-end gap-2">
            <button className="blue-button-teams green-btn" onClick={handleClose}>
              Close
            </button>
          </div>
        )}
      </Modal.Footer>
    </Modal>
  );
};

CustomsMoPConfirmationModal.propTypes = {
  type: PropTypes.string.isRequired,
  onHide: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  initialSelection: PropTypes.number,
  readOnly: PropTypes.bool,
  useTanAccount: PropTypes.bool.isRequired,
};

export default CustomsMoPConfirmationModal;
